import { Header, LoadingSpinner } from '@components/Generic'
import { observer } from 'mobx-react'
import { type ReactNode, useContext, useEffect, useMemo, useState, Fragment } from 'react'
import { StoreContext } from '@store/Root.store'

import CountdownTimer from '@/components/CountdownTimer/CountdownTimer'
import { axiosInstance } from '@/methods/axiosConfig'
import { forceRedirect } from '@/methods/forceRedirect'
import { useInterval } from 'usehooks-ts'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import { devLog } from '@/methods/devLog'
import { API_URL, ONBOARDING_SERVICE_PATH } from '@constants/apiUrl'
import { Illustration } from '@/components/Illustration/Illustration'

interface PollingResponse {
  status: string
  shouldWait: boolean
  proceedUrl?: string
}

export const IdentityVerificationEnpalPage = observer(() => {
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { IdentityVerificationEnpalPage: trans } = store.TranslationsState.translations

  const [shouldWait, setShouldWait] = useState(true)
  const [shouldPoll, setShouldPoll] = useState(true)
  const [countdownFinished, setCountdownFinished] = useState(false)

  const isVisible = usePageVisibility()

  const checkStatus = async (): Promise<PollingResponse | undefined> => {
    try {
      const res = await axiosInstance.get<PollingResponse>(
        `${API_URL}/${ONBOARDING_SERVICE_PATH}/waiting`,
        {
          withCredentials: true,
        }
      )

      if (!res.data.shouldWait && res?.data?.proceedUrl) {
        setShouldWait(false)

        sessionStorage.removeItem('finalTimestamp')
        forceRedirect(res.data.proceedUrl)
      }

      return res.data
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    setShouldPoll(isVisible)
  }, [isVisible])

  useEffect(() => {
    shouldPoll && checkStatus()
  }, [shouldPoll])

  useInterval(checkStatus, shouldWait && shouldPoll ? 3000 : null)

  const WrapperComponent = useMemo(() => {
    return !isMobile
      ? Fragment
      : (props: { children?: ReactNode }) => (
          <div className="layout__vertically-centered-container">{props.children}</div>
        )
  }, [isMobile])

  return (
    <div className="page-container mobile-m-0">
      {!countdownFinished ? (
        <WrapperComponent>
          <Illustration type="identity-verification" />
          <Header>{trans.header}</Header>
          <p className="generic__paragraph-centered">{trans.message}</p>

          <CountdownTimer
            countdownSeconds={5 * 60}
            onComplete={() => setCountdownFinished(true)}
            trans={trans}
          />
        </WrapperComponent>
      ) : (
        <WrapperComponent>
          <Header>{trans.header}</Header>
          <p className="generic__paragraph-centered">{trans.moreTimeNeeded}...</p>

          <LoadingSpinner className="loading-spinner--no-padding" />

          <p className="generic__paragraph-centered">{trans.pleaseWait}...</p>
        </WrapperComponent>
      )}
    </div>
  )
})
